import React, { useCallback, useEffect, useRef, useState } from "react";
import Cookies from "js-cookie";
import { useNavigate } from "react-router-dom";
import InfiniteScroll from "react-infinite-scroll-component";
import lottie from "lottie-web";
import { Helmet } from "react-helmet-async";

import Post from "../components/Post";
import Footer from "../components/Footer";
import ReportFab from "../components/ReportFab";
import CategoryStrip from "../components/CategoryStrip";
import BleepSpinnerDark from "../components/lottie/BleepSpinnerDark.json";

const PAGE_SIZE = 12;

// The full, infinitely-scrolling feed. Home links here from three places; it
// keeps the home page free for explaining the platform.
const Bleeps = ({ domain, userData }) => {
  const userId = userData?.[0] ?? Cookies.get("userId");
  const userType = userData?.[1] ?? Cookies.get("userType");
  const canReport = userId == null || userType === "user";

  const navigate = useNavigate();

  const [posts, setPosts] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [showFab, setShowFab] = useState(false);
  const fetchingRef = useRef(false);
  const spinnerRef = useRef(null);

  const buildUrl = useCallback(
    (pageNumber) =>
      `/api/posts?page=${pageNumber}&size=${PAGE_SIZE}` +
      (userId ? `&user=${userId}` : ""),
    [userId],
  );

  /* ---------------- First page (and refetch when auth hydrates) ------------ */
  const loadFirstPage = useCallback(async () => {
    setIsLoading(true);
    fetchingRef.current = true;
    try {
      const res = await fetch(buildUrl(1));
      const data = await res.json();
      const list = Array.isArray(data) ? data : [];
      setPosts(list);
      setHasMore(list.length === PAGE_SIZE);
      setPage(2);
    } catch (err) {
      console.error("Error fetching bleeps:", err);
      setHasMore(false);
    } finally {
      setIsLoading(false);
      fetchingRef.current = false;
    }
  }, [buildUrl]);

  useEffect(() => {
    loadFirstPage();
  }, [loadFirstPage]);

  /* ---------------- Subsequent pages -------------------------------------- */
  const fetchMore = async () => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    try {
      const res = await fetch(buildUrl(page));
      const data = await res.json();
      const list = Array.isArray(data) ? data : [];
      setPosts((prev) => [...prev, ...list]);
      setHasMore(list.length === PAGE_SIZE);
      setPage((prev) => prev + 1);
    } catch (err) {
      console.error("Error fetching bleeps:", err);
      setHasMore(false);
    } finally {
      fetchingRef.current = false;
    }
  };

  /* ---------------- Spinner ------------------------------------------------ */
  useEffect(() => {
    if (spinnerRef.current && !spinnerRef.current._loaded) {
      spinnerRef.current._loaded = true;
      lottie.loadAnimation({
        container: spinnerRef.current,
        renderer: "canvas",
        loop: true,
        autoplay: true,
        animationData: BleepSpinnerDark,
      });
    }
  });

  /* ---------------- Floating button ---------------------------------------- */
  useEffect(() => {
    const onScroll = () => setShowFab(window.scrollY > 200);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="bleep-light bl-page bl-page--dark">
      <Helmet>
        <title>Latest Bleeps | BLEEP</title>
        <meta
          name="description"
          content="Browse the latest counterfeit reports filed by the BLEEP community across every sector."
        />
      </Helmet>

      {/* No hero. This is a browse surface, so the title sits on the same dark
          band as the chips and the feed and the content starts immediately. */}
      <section className="bl-section bl-section--dark">
        {/* No report button up here: the floating one covers the whole page. */}
        <div className="bl-shell bl-feed-head">
          <div className="bl-head-row">
            <h1 className="bl-page-title">Latest bleeps</h1>
            {/* Home rather than navigate(-1): this page is reachable from the
                navbar on any page, so "back" could be anywhere, and the feed is
                a place you leave for the homepage rather than one you arrive at
                mid-task. */}
            <button
              type="button"
              className="bl-close-btn"
              onClick={() => navigate("/")}
              aria-label="Close and go to the homepage"
            >
              <i
                className="fas fa-xmark"
                aria-hidden="true"
              />
            </button>
          </div>
          <p className="bl-page-sub">
            Every counterfeit report filed by the community, newest first.
          </p>
        </div>

        <div className="bl-strip-wrap">
          <CategoryStrip />
        </div>

        <div className="bl-shell bl-shell--spaced">
          {isLoading && posts.length === 0 ? (
            <div className="row row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-lg-4 g-3">
              {Array.from({ length: PAGE_SIZE }).map((_, i) => (
                <div className="col pb-3" key={i}>
                  <div className="bleep-post-skeleton bl-pulse">
                    <div className="bleep-post-skeleton__media" />
                    <div className="bleep-post-skeleton__body">
                      <div className="bleep-post-skeleton__line" />
                      <div
                        className="bleep-post-skeleton__line"
                        style={{ width: "80%" }}
                      />
                      <div
                        className="bleep-post-skeleton__line"
                        style={{ width: "45%" }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <InfiniteScroll
              dataLength={posts.length}
              next={fetchMore}
              hasMore={hasMore}
              style={{ overflow: "unset" }}
              loader={
                <div
                  style={{ display: "flex", justifyContent: "center" }}
                  aria-label="Loading more bleeps"
                >
                  <div ref={spinnerRef} style={{ width: 140, height: 140 }} />
                </div>
              }
              endMessage={
                <p className="bl-end-message">
                  You've reached the beginning{" "}
                  <i className="fas fa-glass-cheers" />
                </p>
              }
            >
              <div className="row row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-lg-4 g-3">
                {posts.map((post) => (
                  <Post key={post._id} post={post} />
                ))}
              </div>
            </InfiniteScroll>
          )}
        </div>
      </section>

      {canReport && (
        <ReportFab visible={showFab} onClick={() => navigate("/report")} />
      )}

      <Footer domain={domain} />
    </div>
  );
};

export default Bleeps;
